<template>
  <div>
    <div class="d-flex justify-content-center align-items-center mb-1">
      <b-badge
        variant="light-warning"
        class="crm-banner py-1"
      >
        This is a beta version of the CRM system
      </b-badge>
    </div>
    <FilterCard :applied-filter-count="appliedFilterCount">
      <b-row>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group
            label="Ticket ID"
            label-for="ticket_id"
          >
            <b-form-input
              id="ticket_id"
              v-model="filters['ticket_id']"
              placeholder="Search"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group
            label="Waybill Number"
            label-for="waybill_number"
          >
            <b-form-input
              id="waybill_number"
              v-model="filters['waybill_number']"
              placeholder="Search"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Ticket Status"
            label-for="ticket_status"
          >
            <v-select
              v-model="selectedStatusFilters"
              :options="statusOptions"
              :reduce="option => option.value"
              label="name"
              placeholder="Select Status"
              multiple
              deselect-from-dropdown
            >
              <template
                v-slot:option="option"
                class="text-truncate"
              >
                {{ option.name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Created Date"
            label-for="sent_date"
          >
            <div class="position-relative">
              <AdvancedDateFilter
                :date-value="filters['created_at']"
                :opens="'left'"
                @update="updateDateRange('created_at', $event)"
              />
            </div>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="Last Updated"
            label-for="last_updated"
          >
            <div class="position-relative">
              <AdvancedDateFilter
                :date-value="filters['last_updated']"
                :opens="'right'"
                @update="updateDateRange('last_updated', $event)"
              />
            </div>
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group
            label="Merchant"
            label-for="merchant"
          >
            <v-select
              v-model="selectedMerchantFilters"
              :options="merchantOptions"
              :reduce="option => option.id"
              label="name"
              placeholder="Select Merchant"
              multiple
              deselect-from-dropdown
            >
              <template v-slot:option="option">
                {{ option.name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group
            label="Ticket Category"
            label-for="ticket-category"
          >
            <v-select
              v-model="filters['ticket_type_id']"
              :options="ticketTypes"
              :reduce="option => option.id"
              label="name"
              placeholder="Select Ticket Category"
            >
              <template v-slot:option="option">
                {{ option.name }}
              </template>
            </v-select>
          </b-form-group>
        </b-col>
      </b-row>
      <!-- Apply and Clear Filters -->
      <b-row class="mt-1">
        <b-col>
          <filter-buttons
            :filter-data="filters"
            :apply-filters="applyFilters"
            :clear-filters="clearFilters"
          />
        </b-col>
      </b-row>
    </FilterCard>

    <b-card>
      <b-row>
        <b-col>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            size="sm"
            variant="outline-primary"
            class="refresh-button d-flex align-items-center justify-content-center shadow-custom"
            :disabled="loading"
            :style="cardBorder"
            @click="refreshTicketSummery()"
          >
            <feather-icon
              icon="RefreshCwIcon"
              class="mr-50 refresh-icon"
            />
            <span class="align-middle refresh-span">Refreshed</span>
            <time-ago
              class="timeAgo"
              :datetime="refreshedDate"
              refresh
              locale="en"
              :long="longString"
              :style="{color: 'black'}"
            />
          </b-button>
        </b-col>
      </b-row>
      <b-row class="mt-1">
        <summary-card
          title="OPEN"
          :sum="ticketOptions.open"
          :border-variant="selection === 'open'? '' :'primary'"
          :bg-variant="selection === 'open'? 'var(--primary)' :''"
          :text-variant="selection === 'open'? 'white' :'#5c5b61'"
          @click.native="onSummaryCardClick('open')"
        />
        <summary-card
          title="ASSIGNED"
          :sum="ticketOptions.assigned"
          :border-variant="selection === 'assigned'? '' :'primary'"
          :bg-variant="selection === 'assigned'? 'var(--primary)' :''"
          :text-variant="selection === 'assigned'? 'white' :'#5c5b61'"
          @click.native="onSummaryCardClick('assigned')"
        />
        <summary-card
          title="IN PROGRESS"
          :sum="ticketOptions.in_progress"
          :border-variant="selection === 'in_progress'? '' :'primary'"
          :bg-variant="selection === 'in_progress'? 'var(--primary)' :''"
          :text-variant="selection === 'in_progress'? 'white' :'#5c5b61'"
          @click.native="onSummaryCardClick('in_progress')"
        />
        <summary-card
          title="ON HOLD"
          :sum="ticketOptions.on_hold"
          :border-variant="selection === 'on_hold'? '' :'primary'"
          :bg-variant="selection === 'on_hold'? 'var(--primary)' :''"
          :text-variant="selection === 'on_hold'? 'white' :'#5c5b61'"
          @click.native="onSummaryCardClick('on_hold')"
        />
        <summary-card
          title="RESOLVED"
          :sum="ticketOptions.resolved"
          :border-variant="selection === 'resolved'? '' :'primary'"
          :bg-variant="selection === 'resolved'? 'var(--primary)' :''"
          :text-variant="selection === 'resolved'? 'white' :'#5c5b61'"
          @click.native="onSummaryCardClick('resolved')"
        />
        <summary-card
          title="CLOSED"
          :sum="ticketOptions.closed"
          :border-variant="selection === 'closed'? '' :'primary'"
          :bg-variant="selection === 'closed'? 'var(--primary)' :''"
          :text-variant="selection === 'closed'? 'white' :'#5c5b61'"
          @click.native="onSummaryCardClick('closed')"
        />
      </b-row>
    </b-card>

    <b-card
      no-body
    >
      <div>
        <div
          class="mx-2 mt-2 mb-1"
        >
          <b-row class="d-sm-flex align-items-start">
            <b-button
              v-can="'create-tickets'"
              v-ripple.400="'rgba(113, 102, 240, 0.15)'"
              variant="primary"
              class="mobile-margin"
              @click="onClickCreate"
            >
              <feather-icon
                icon="PlusIcon"
              />
              Add New
            </b-button>
            <b-col
              class="d-flex flex-wrap justify-content-end align-items-center mb-1 mb-md-0"
            >
              <ReportTableButtons
                :items="rows"
                :json_fieldz="json_fields"
                :name="`Tickets - Page ${page}.xls`"
                :bulkname="`Tickets.xls`"
                :fetch="getTicketListNoPagination"
                @refresh="onClickRefresh"
              />
            </b-col>
          </b-row>
        </div>

        <!-- table -->
        <b-overlay
          id="overlay-background"
          variant="light"
          opacity="0.30"
          blur="10px"
          rounded="sm"
        >
          <b-table
            id="ticket-table"
            striped
            hover
            responsive
            :per-page="perPage"
            :items="rows"
            :fields="fields"
            show-empty
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="rows"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div
                class="sortable-header"
              >
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <template #cell(id)="data">
              <div style="width: 100px;">
                <router-link
                  class="text-primary font-weight-bolder"
                  :to="{ name: 'ticket-details', params: { id: data.item.id } }"
                >
                  {{ `Ref - ${data.value}` }}
                </router-link>
              </div>
            </template>
            <template #cell(waybill_number)="data">
              <div
                class="d-flex align-items-center"
              >
                <div
                  v-if="data.value"
                  class="font-weight-bolder text-primary"
                  @click="onClickViewOrder(data.item.order_id)"
                >
                  {{ data.value }}
                </div>
                <div v-else>
                  {{ '---' }}
                </div>
              </div>
            </template>
            <template #cell(destination_warehouse)="data">
              {{ data.value.name || 'N/A' }}
            </template>
            <template #cell(type)="data">
              {{ data.value.name }}
            </template>
            <template #cell(merchant)="data">
              <div>
                {{ data.value ? data.value.name : '---' }}
              </div>
              <div>
                {{ merchantContactNumber(data.item) }}
              </div>
            </template>
            <template #cell(created_by)="data">
              <div>
                {{ data.value ? data.value.first_name + ' ' + (data.value.last_name || '') : '---' }}
              </div>
            </template>
            <template #cell(reported_by)="data">
              <div>
                {{ data.value ? data.value.charAt(0).toUpperCase() + data.value.slice(1) : '---' }}
              </div>
            </template>
            <template #cell(contact_details)="data">
              <div>
                {{ reportedPersonName(data.item) }}
              </div>
              <div>
                {{ reportedPersonPhone(data.item) }}
              </div>
            </template>
            <template #cell(assigned_by)="data">
              <div>
                {{ data.value ? data.value.first_name + ' ' + (data.value.last_name || '') : '---' }}
              </div>
            </template>
            <template #cell(order_current_status.key)="data">
              <div>
                <order-status-badge :status-key="data.value" />
              </div>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusColor(data.value)">
                {{ statusName(data.value) }}
              </b-badge>
            </template>
            <template #cell(created_at)="data">
              <div>
                {{ __dateTimeFormatter(data.value) }}
              </div>
            </template>
            <template #cell(last_updated)="data">
              <div
                v-if="data.value"
                style="width: 150px;"
              >
                {{ __dateTimeFormatterPassedTime(data.value) }}
              </div>
              <div v-else>
                -
              </div>
            </template>
            <template #cell(action)="data">
              <b-dropdown
                variant="link"
                toggle-class="text-decoration-none"
                no-caret
              >
                <template v-slot:button-content>
                  <feather-icon
                    icon="MoreVerticalIcon"
                    size="12"
                    class="text-body align-middle mr-25"
                  />
                </template>
                <b-dropdown-item
                  v-can="'view-tickets'"
                  :to="{ name: 'ticket-details', params: { id: data.item.id } }"
                >
                  <feather-icon icon="EyeIcon" />
                  View
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
          <div class="mx-2 mb-2">
            <b-row
              cols="12"
            >
              <b-col
                class="d-flex align-items-center justify-content-sm-start"
                md="3"
              >
                <label class="width-75">Per page</label>
                <v-select
                  v-model="perPage"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="pageOptions"
                  :clearable="false"
                  class="per-page-selector d-inline-block width-100"
                />
              </b-col>
              <!-- Pagination -->
              <b-col
                class="d-flex align-items-center justify-content-sm-end"
                md="9"
              >
                <span class="text-muted pagination-text  mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
                <b-pagination
                  v-model="page"
                  :per-page="perPage"
                  :total-rows="(meta.total)? meta.total : 0"
                  class="mb-0 mt-1 mt-sm-0"
                  first-number
                  last-number
                  next-class="next-item"
                  prev-class="prev-item"
                >
                  <template #prev-text>
                    <feather-icon
                      icon="ChevronLeftIcon"
                      size="18"
                    />
                  </template>
                  <template #next-text>
                    <feather-icon
                      icon="ChevronRightIcon"
                      size="18"
                    />
                  </template>
                </b-pagination>
              </b-col>
            </b-row>
          </div>
        </b-overlay>
      </div>
    </b-card>
    <create-ticket-modal ref="set_ticket" />
    <view-order-modal ref="viewOrderModal" />
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BFormGroup,
  BPagination,
  BRow,
  BOverlay,
  VBModal,
  BTable,
  BDropdown,
  BDropdownItem,
  VBTooltip,
  BButton,
  BFormInput,
  BBadge,
  // BAvatar,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import Filter from '@core/mixins/filter'
import vSelect from 'vue-select'
import { TimeAgo } from 'vue2-timeago'
import AdvancedDateFilter from '@/components/AdvancedDateFilter/AdvancedDateFilter.vue'
import { avatarText } from '@core/utils/filter'
import CreateTicketModal from '@/views/tickets/CreateTicketModal.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import store from '@/store/index'
import FilterButtons from '@/components/Filter/FilterButtons.vue'
import SummaryCard from '@core/components/summary-card/TicketSummaryCard.vue'
import ViewOrderModal from '@/components/AllOrders/ViewOrderModal.vue'
import tableMixin from '@core/mixins/tableMixin'
import FilterCard from '@/components/FilterCard/FilterCard.vue'
import OrderStatusBadge from '@core/components/status/OrderStatusBadge.vue'

const ticketRepository = RepositoryFactory.get('ticket')
const merchantRepository = RepositoryFactory.get('merchant')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    BPagination,
    BFormGroup,
    BCard,
    BRow,
    BCol,
    BTable,
    BOverlay,
    vSelect,
    AdvancedDateFilter,
    BDropdown,
    BDropdownItem,
    BButton,
    BFormInput,
    FilterCard,
    BBadge,
    // BAvatar,
    CreateTicketModal,
    FilterButtons,
    SummaryCard,
    ViewOrderModal,
    TimeAgo,
    OrderStatusBadge,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      // Filters Start
      filters: {
        status: 'open,',
        created_at: {
          startDate: null,
          endDate: null,
        },
        last_updated: {
          startDate: null,
          endDate: null,
        },
      },
      // Filters End
      longString: true,
      refreshedDate: new Date(),
      selection: 'open',
      avatarText,
      total: 0,
      showModal: false,
      meta: {},
      dataLoading: false,
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50],
      totalRows: 1,
      page: 1,
      ticketTypes: [],
      merchantOptions: [],
      selectedMerchantFilters: [],
      selectedStatusFilters: ['open'],
      statusOptions: [
        { name: 'Open', value: 'open', variant: 'light-primary' },
        { name: 'Assigned', value: 'assigned', variant: 'light-info' },
        { name: 'In Progress', value: 'in_progress', variant: 'light-warning' },
        { name: 'On Hold', value: 'on_hold', variant: 'light-secondary' },
        { name: 'Resolved', value: 'resolved', variant: 'light-success' },
        { name: 'Closed', value: 'closed', variant: 'light-danger' },
      ],
      fields: [
        {
          key: 'id',
          label: 'Ticket ID',
          sortable: true,
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'merchant',
          label: 'Merchant',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'subject',
          label: 'Subject',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'waybill_number',
          label: 'WayBill Number',
          sortable: true,
        },
        {
          key: 'destination_warehouse',
          label: 'Destination Warehouse',
          sortable: true,
          thClass: ' text-left',
          tdClass: ' text-left',
        },
        {
          key: 'created_by',
          label: 'Added By',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'reported_by',
          label: 'Reported By',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'contact_details',
          label: 'Contact Details',
          thClass: ' text-left',
          tdClass: ' text-left',
        },
        {
          key: 'assigned_by',
          label: 'Assignee Name',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'assigned_role.name',
          label: 'Assignee Role',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'type.name',
          label: 'Ticket Category',
          thClass: 'text-left',
          tdClass: 'text-left',
        },
        {
          key: 'order_current_status.key',
          label: 'Order Status',
          sortable: false,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'status',
          label: 'Ticket Status',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'created_at',
          label: 'Created Date',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'last_updated',
          label: 'Last Updated',
          sortable: true,
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          key: 'action',
          label: 'Action',
          sortable: false,
        },
      ],
      rows: [],
      json_fields: {
        'Ticket ID': {
          field: 'id',
          callback: value => `"Ref - ${value}"`,
        },
        Merchant: {
          field: 'merchant',
          callback: value => `"${value.name}"`,
        },
        'Merchant Contact Number': {
          field: 'merchant_admin',
          callback: value => (value && value.phone ? `"${value.phone}"` : '""'),
        },
        Subject: {
          field: 'subject',
          callback: value => `"${value}"`,
        },
        'Details Description': {
          field: 'description',
          callback: value => `"${value}"`,
        },
        'Waybill Number': 'waybill_number',
        'Destination Warehouse': {
          field: 'destination_warehouse.name',
          callback: value => `"${value}"`,
        },
        'Added By': {
          field: 'created_by',
          callback: value => (value ? `"${(`${value.first_name} ${value.last_name || ''}`).trim()}"` : '""'),
        },
        'Reported By': {
          field: 'reported_by',
          callback: value => `"${value}"`,
        },
        'Reported Person Name': {
          callback: row => `"${this.reportedPersonName(row) === '---' ? '' : this.reportedPersonName(row)}"`,
        },
        'Reported Person Phone': {
          callback: row => `"${this.reportedPersonPhone(row) === '---' ? '' : this.reportedPersonPhone(row)}"`,
        },
        'Assignee Name': {
          field: 'assigned_by',
          callback: value => (value ? `"${(`${value.first_name} ${value.last_name || ''}`).trim()}"` : '""'),
        },
        'Assignee ID': {
          field: 'assigned_by',
          callback: value => `"${value.reference_id}"`,
        },
        'Assignee Role': {
          field: 'assigned_role',
          callback: value => `"${value.name}"`,
        },
        'Ticket Category': {
          field: 'type',
          callback: value => `"${value.name}"`,
        },
        'Order Status': 'order_current_status.name',
        'Ticket Status': {
          field: 'status',
          callback: value => `"${value}"`,
        },
        Created: {
          field: 'created_at',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
        Updated: {
          field: 'last_updated',
          // eslint-disable-next-line no-underscore-dangle
          callback: value => this.__dateTimeFormatter(value),
        },
      },
      ticketOptions: [],
      loading: false,
    }
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true
        return this.dir
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false
      return this.dir
    },
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    cardBorder() {
      return {
        border: '1px solid var(--primary) !important',
      }
    },
  },
  watch: {
    page() {
      this.getTicketList()
    },
    perPage() {
      this.getTicketList()
    },
    selectedStatusFilters: {
      async handler(val) {
        let keyList = ''
        val.forEach(key => {
          keyList += `${key},`
        })
        this.filters = {
          ...this.filters,
          status: keyList,
        }
      },
      deep: true,
    },
    selectedMerchantFilters: {
      async handler(val) {
        let keyList = ''
        val.forEach(key => {
          keyList += `${key},`
        })
        this.filters = {
          ...this.filters,
          merchant_id: keyList,
        }
      },
      deep: true,
    },
  },
  async mounted() {
    this.totalRows = this.rows.length
    this.getTicketSummary()
    this.getTicketTypes()
    this.getMerchantList()
    this.getTicketList()
    this.updateFilterCount()
  },

  methods: {
    async getTicketTypes() {
      try {
        this.ticketTypes = localStorage.getItem('ticket_types') ? JSON.parse(localStorage.getItem('ticket_types')) : (await ticketRepository.getTicketTypes()).data.data
        if (!localStorage.getItem('ticket_types')) localStorage.setItem('ticket_types', JSON.stringify(this.ticketTypes))
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async getMerchantList() {
      this.merchantOptions = localStorage.getItem('merchant_list') ? JSON.parse(localStorage.getItem('merchant_list')) : (await merchantRepository.getMerchantListForDropdown()).data.data
      if (!localStorage.getItem('merchant_list')) localStorage.setItem('merchant_list', JSON.stringify(this.merchantOptions))
    },
    async getTicketList() {
      this.dataLoading = true
      this.rows = []
      const filterQuery = this.buildFilterQuery()
      try {
        const { data } = (await ticketRepository.getAllTickets(this.page, filterQuery, this.perPage))
        this.rows = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }
      this.dataLoading = false
    },
    async getTicketListNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await ticketRepository.getAllTicketsNoPagination(filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async getTicketSummary() {
      try {
        const summaryData = localStorage.getItem('ticket-summery')
        const refreshDate = localStorage.getItem('ticket-refresh-time')

        if (summaryData && refreshDate) {
          // Parse the JSON string back into a JavaScript object
          this.ticketSummery = JSON.parse(summaryData)
          this.refreshedDate = JSON.parse(refreshDate)
        } else {
          const { data } = (await ticketRepository.getTicketSummary()).data
          this.ticketSummery = data
          this.refreshedDate = new Date()

          localStorage.setItem('ticket-refresh-time', JSON.stringify(this.refreshedDate))
          // Convert the data to a JSON string and save it in local storage
          localStorage.setItem('ticket-summery', JSON.stringify(this.ticketSummery))
        }
        this.ticketOptions = this.ticketSummery
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    async onSummaryCardClick(selection) {
      this.selectedStatusFilters = []
      this.selection = selection
      this.$nextTick(() => {
        this.applyFilters()
      })
      this.selectedStatusFilters.push(selection)
    },
    async openCreateTicketModal() {
      this.$refs.createTicketModal.openModal()
    },
    // REQUIRED
    applyFilters() {
      this.getTicketList()
      this.updateFilterCount()
    },
    onClickCreate() {
      this.$refs.set_ticket.openModal(null, 'add')
    },
    clearFilters() {
      this.selection = ''
      this.selectedStatusFilters = []
      this.selectedMerchantFilters = ''
      this.filters = {
        created_at: {
          startDate: null,
          endDate: null,
        },
        last_updated: {
          startDate: null,
          endDate: null,
        },
      }
      this.$nextTick(() => {
        this.getTicketList()
      })
      this.updateFilterCount()
    },
    handlePaste() {
      setTimeout(() => {
        this.getTicketList()
      }, 0)
    },
    handleChangePage(page) {
      this.page = page
      this.getTicketList()
    },
    changePage(value) {
      this.perPage = value
    },
    // REQUIRED
    sortChanged(e) {
      const key = e.sortBy
      if (key) {
        const active = e.sortDesc ? '-' : ''
        this.sort = active + key
        this.getTicketList()
      }
    },
    onClickRefresh() {
      this.getTicketSummary()
      this.getTicketList()
    },
    refreshTicketSummery() {
      this.loading = true
      localStorage.removeItem('ticket-summery')
      localStorage.removeItem('ticket-refresh-time')
      this.getTicketSummary().then(() => {
        this.loading = false
      })
    },
    onClickViewOrder(id) {
      this.$refs.viewOrderModal.openModal(id)
    },
    statusColor(value) {
      return this.statusOptions.find(n => n.value === value).variant
    },
    statusName(value) {
      return this.statusOptions.find(n => n.value === value).name
    },
    merchantContactNumber(item) {
      if (item.merchant_admin && item.merchant_admin.phone) {
        return item.merchant_admin.phone
      }
      if (item.reported_by === 'merchant' && item.reported_person_phone) {
        return item.reported_person_phone
      }
      return '---'
    },
    reportedPersonName(item) {
      if (item.reported_person_name) {
        return item.reported_person_name
      }
      if (item.reported_by === 'merchant' && item.merchant_admin) {
        const admin = item.merchant_admin
        const name = admin.name || `${admin.first_name || ''} ${admin.last_name || ''}`.trim()
        return name || '---'
      }
      return '---'
    },
    reportedPersonPhone(item) {
      if (item.reported_person_phone) {
        return item.reported_person_phone
      }
      if (item.reported_by === 'merchant' && item.merchant_admin && item.merchant_admin.phone) {
        return item.merchant_admin.phone
      }
      return '---'
    },
  },
}
</script>
<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';
.refresh-button {
  float: right;
}
.refresh-icon {
  color: black !important;
}
.refresh-span {
  margin-bottom: 1px;
  color: black !important;
  font-size: 14px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.timeAgo {
  margin-left: 5px;
  font-size: 13px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}
.shadow-custom {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
}
.search-button {
  margin-top: 1.5rem;
}
.crm-banner {
  font-size: 1rem;
  width: 100%;
}
@media (max-width: 767px) {
  #ticket-table thead th {
    position: sticky;
    top: 0;
  }
  .waybill-search .search-button {
    margin-top: 0 !important;
  }
}
</style>
